'use client'

import { useRef, useState, useEffect } from 'react'
import clsx from 'clsx'
import type { TimelineSectionFragment } from '@/lib/types/datocms/graphql/generated'
import Eyebrow from '../ui/Eyebrow'
import TimelineCard from '../TimelineCard'
import IconButton from '../ui/IconButton'
import { TextWithHighlight } from '../TextWithHighlight'
import { isStructuredTextEmpty } from '@/utils/datocms/isStructuredTextEmpty'

export default function TimelineSection({
	title,
	tag,
	timelineCards,
}: Readonly<TimelineSectionFragment>) {
	const sliderContainerRef = useRef<HTMLDivElement>(null)
	const [cardWidth, setCardWidth] = useState<number>(455)
	const [isAtStart, setIsAtStart] = useState<boolean>(true)
	const [isAtEnd, setIsAtEnd] = useState<boolean>(false)
	const [activeCard, setActiveCard] = useState<number>(0)

	const handleScroll = (direction: number) => {
		if (activeCard + direction >= 0 && activeCard + direction < timelineCards.length) {
			setActiveCard(activeCard + direction)
		}

		if (activeCard >= 1 && sliderContainerRef.current) {
			sliderContainerRef.current.scrollBy(direction * cardWidth, 0)
			setTimeout(checkCarouselPosition, 300)
		}
	}

	const handleCardClick = (index: number) => {
		if (index === activeCard) return
		setActiveCard(index)
		if (sliderContainerRef.current) {
			sliderContainerRef.current.scrollTo({
				left: index * cardWidth,
				behavior: 'smooth',
			})
			setTimeout(checkCarouselPosition, 300)
		}
	}

	useEffect(() => {
		const updateCardWidth = () => {
			const firstCard = sliderContainerRef.current?.querySelector('.carousel-card')
			if (firstCard) {
				setCardWidth(firstCard.clientWidth)
			}
		}
		updateCardWidth()
		window.addEventListener('resize', updateCardWidth)
		return () => {
			window.removeEventListener('resize', updateCardWidth)
		}
	}, [])

	useEffect(() => {
		const handleScrollPosition = () => {
			if (sliderContainerRef.current) {
				const { scrollLeft } = sliderContainerRef.current
				const offset = cardWidth * 0.99
				const newActiveCard = Math.floor((scrollLeft + offset) / cardWidth)
				if (
					newActiveCard !== activeCard &&
					newActiveCard >= 0 &&
					newActiveCard < timelineCards.length
				) {
					setActiveCard(newActiveCard)
				}
			}
		}

		const container = sliderContainerRef.current
		if (container) {
			container.addEventListener('scroll', handleScrollPosition)
			return () => container.removeEventListener('scroll', handleScrollPosition)
		}
	}, [cardWidth, activeCard, timelineCards.length])

	useEffect(() => {
		checkCarouselPosition()
	}, [cardWidth, activeCard])

	const checkCarouselPosition = () => {
		if (sliderContainerRef.current) {
			const { scrollLeft, scrollWidth, clientWidth } = sliderContainerRef.current
			setIsAtStart(scrollLeft === 0 && activeCard === 0)
			setIsAtEnd(scrollLeft + clientWidth >= scrollWidth || activeCard === timelineCards.length - 1)
		}
	}
	return (
		<section className="timeline-section @container">
			<div className="flex flex-col max-w-screen-2xl mx-auto py-3 @lg:py-16 @2xl:py-20">
				<div className="common-x-padding">
					{tag ? <Eyebrow>{tag}</Eyebrow> : null}

					{title && !isStructuredTextEmpty(title.text) && (
						<div className="heading-size-3 mt-4">
							<TextWithHighlight text={title} />
						</div>
					)}
				</div>

				<div
					ref={sliderContainerRef}
					className={clsx(
						'relative flex flex-row overflow-x-auto scroll-bar-mini lg:rounded-xl pb-10 px-3 @lg:px-10 mt-10',
						// gray line
						"before:content-[''] before:absolute before:top-[calc(50%-12px)] before:-translate-y-[46px] before:inset-x-0 before:h-px before:bg-blue-dark/5 before:origin-center"
					)}
				>
					{timelineCards.map((timelineCard, idx) => (
						<TimelineCard
							idx={idx}
							key={timelineCard.id as string}
							activeCard={activeCard}
							onCardClick={handleCardClick}
							{...timelineCard}
						/>
					))}
					{/* Empty spacer card to allow last card to be centered/active */}
					<div className="carousel-card shrink-0 w-0 md:w-1/4 lg:w-1/2" />
				</div>
				<div className="flex gap-2 ml-auto common-x-padding -translate-y-1/2">
					<IconButton
						icon="Arrow"
						className="rotate-180"
						variant="outline-accent"
						aria-label="scroll to previous card"
						onClick={() => handleScroll(-1)}
						disabled={isAtStart}
					/>
					<IconButton
						icon="Arrow"
						variant="outline-accent"
						aria-label="scroll to next card"
						onClick={() => handleScroll(1)}
						disabled={isAtEnd}
					/>
				</div>
			</div>
		</section>
	)
}
